/**
 * The page's reads of the offline store (unit 26A).
 *
 * `useCached(entity)` is how a widget reads a synced entity (`frontend.md`
 * rule 3, Q1 (a)): from the cache the outbox writes to, so a tick shows at once
 * with no signal. It reads again when the store says the cache changed — a
 * write's effect, or a sync, in this page or in the Service Worker.
 */
import { useEffect, useState } from "react";

import { rows, type Entity, type Rows } from "./cache";
import { openOffline } from "./idb";
import { CHANNEL, failures, type Failure } from "./outbox";

/**
 * `read` now, and again whenever the store announces `what`. `read` must be a
 * function of `key` alone: it is read again when `key` changes, not when a new
 * closure arrives.
 */
function useStoreRead<T>(
  what: "cache" | "failures",
  key: string,
  read: (db: IDBDatabase) => Promise<T>,
): T | undefined {
  const [value, setValue] = useState<T>();
  useEffect(() => {
    let live = true;
    const load = async () => {
      const db = await openOffline();
      try {
        const next = await read(db);
        if (live) setValue(next);
      } finally {
        db.close();
      }
    };
    void load();
    const channel = typeof BroadcastChannel === "undefined" ? null : new BroadcastChannel(CHANNEL);
    if (channel) {
      channel.onmessage = (event: MessageEvent<{ type?: string }>) => {
        if (event.data?.type === what) void load();
      };
    }
    return () => {
      live = false;
      channel?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `read` is a function of `key`
  }, [what, key]);
  return value;
}

/** Every cached row of one synced entity; `undefined` until first read. */
export function useCached<E extends Entity>(entity: E): Rows[E][] | undefined {
  return useStoreRead("cache", entity, (db) => rows(db, entity));
}

/** The writes the server refused for good, to be shown (rule 9). */
export function useFailures(): Failure[] | undefined {
  return useStoreRead("failures", "failures", failures);
}
